import type { ReactNode } from 'react'

import { useEffect, useState } from 'react'

import type { ChangeEvent } from '../utils'
import type { RadioProps, RadioStyleProps } from './radio.types'

import Box from '../box'
import { omitThemingProps, styled, th, useStyleConfig, vui } from '../core'
import {
  SelectionControlInput,
  useDeprecatedLgSize,
  useSelectionControlColors,
  useSelectionControlDescription,
} from '../selectionControl/selectionControl'
import T from '../t'
import { cs, filterUndefined } from '../utils'
import { disabled as disabledColors, radioColors } from './consts'
import { useRadioGroupContext } from './context'

// Figma "VUI v5 (modernized)" Radio spec: inner filled dot shown when checked, roughly half the ring's
// diameter (xs=16 -> 8px, sm=20 -> 10px, md/lg=24 -> 12px) — matches Checkbox's mark-inset proportions.
const dotSize: Record<string, string> = { lg: '12px', md: '12px', sm: '10px', xs: '8px' }
// Offset decreases as control size grows, so JSX children stay visually centered with the first text line.
const jsxChildrenMt: Record<string, string> = { lg: '0px', md: '0px', sm: '2px', xs: '3px' }
// Centers the ring on the label's first line: (caption line-height 150% of font-size - control ring height) / 2.
const labelMt: Record<string, string> = { lg: '0px', md: '0px', sm: '0.5px', xs: '1px' }

const RadioControl = styled.spanBox`
  align-items: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  position: relative;
  transition-duration: fast;
`

// Plain CSS dot for the checked state — no icon asset involved. Inherits `color` from `RadioControl`,
// so it automatically matches the ring's current colour (checked/hover/pressed/disabled).
const RadioDot = styled.spanBox`
  background-color: currentColor;
  border-radius: 50%;
  flex-shrink: 0;
`

export const RadioBase = styled.labelBox<RadioStyleProps>`
  cursor: pointer;
  display: inline-flex;
  width: fit-content;

  &:not([aria-disabled='true']):not([aria-readonly='true']):hover .vui-radioControl {
    color: ${p => th.color(p.controlHoverColor)};
  }

  &:not([aria-disabled='true']):not([aria-readonly='true']):active .vui-radioControl {
    color: ${p => th.color(p.controlPressedColor)};
  }

  &[aria-readonly='true'] {
    cursor: auto;
  }

  &:not([aria-disabled='true']):not([aria-readonly='true']) .vui-radioControl:focus-within {
    color: ${radioColors.focus};
  }

  &[aria-disabled='true'] {
    color: ${disabledColors.text};
    cursor: not-allowed;

    .vui-radioControl {
      color: ${disabledColors.icon};
    }
  }
`

/**
 * Allows selection of a single choice from a set of items. Handles controlled and uncontrolled modes.
 * The ring and the inner dot shown when checked are both plain CSS — no icon assets involved.
 */
export const Radio = vui<'span', RadioProps>((props, ref) => {
  const {
    defaultValue: groupDefaultValue,
    isChecked: groupIsChecked,
    onChange: groupOnChange,
    value: groupValue,
    ...radioGroupProps
  } = useRadioGroupContext() ?? {}
  const defaultIsChecked = groupIsChecked !== undefined ? groupIsChecked === props.value : undefined

  const mergedProps = { ...radioGroupProps, ...props }
  const {
    checked = groupValue !== undefined ? props.value === groupValue : undefined,
    children,
    className,
    defaultChecked = groupDefaultValue !== undefined ? props.value === groupDefaultValue : undefined,
    disabled,
    errorText,
    readOnly,
    helpText,
    id: idProp,
    inputProps,
    inputRef,
    label,
    name,
    onChange,
    required,
    value,
    ...rest
  } = omitThemingProps(mergedProps)

  const [isChecked, setIsChecked] = useState(defaultIsChecked ?? checked ?? defaultChecked)
  const styles = useStyleConfig('Radio', mergedProps)
  const { color: controlColor, hoverColor, pressedColor, ...controlStyles } = styles.control
  const size = mergedProps.size ?? 'md'
  const {
    description,
    descriptionId,
    errorText: effectiveErrorText,
    id,
  } = useSelectionControlDescription(idProp, helpText, errorText, !!isChecked)

  const hasJsxChildren = !!children
  const controlMr = children || label ? 1 : 0
  const { color, controlHoverColor, controlPressedColor, descriptionColor, labelColor } = useSelectionControlColors({
    controlColor,
    disabled,
    errorText: effectiveErrorText,
    hoverColor,
    isActive: !!isChecked,
    pressedColor,
    readOnly,
  })

  useDeprecatedLgSize(size, 'Radio')

  useEffect(() => {
    if (groupIsChecked !== undefined) setIsChecked(groupIsChecked === value)
    else if (checked !== undefined) setIsChecked(checked)
  }, [checked, groupIsChecked])

  function handleOnChange(e: ChangeEvent) {
    if (readOnly) return
    // Uncontrolled and outside a group (or group hasn't settled on a value yet): manage checked state locally.
    if (checked === undefined && groupIsChecked === undefined) setIsChecked(e.target.checked)
    groupOnChange?.(e)
    onChange?.(e)
  }

  const aliasedProps = filterUndefined({
    'aria-disabled': disabled,
    'aria-readonly': readOnly,
  })

  return (
    <RadioBase
      alignItems={label || hasJsxChildren ? 'flex-start' : 'center'}
      className={cs('vui-radio', className)}
      controlHoverColor={controlHoverColor}
      controlPressedColor={controlPressedColor}
      ref={ref}
      {...styles.container}
      {...aliasedProps}
      {...rest}
    >
      <RadioControl
        bg="white"
        className="vui-radioControl"
        color={color}
        focusWithinRing={3}
        mr={controlMr}
        mt={
          hasJsxChildren ? (jsxChildrenMt[size] ?? jsxChildrenMt.md) : label ? (labelMt[size] ?? labelMt.md) : undefined
        }
        {...controlStyles}
      >
        <SelectionControlInput
          aria-describedby={descriptionId}
          aria-invalid={!!effectiveErrorText || undefined}
          className="vui-radioInput"
          onChange={handleOnChange}
          ref={inputRef}
          type="radio"
          {...{ checked, defaultChecked, disabled, id, name, required, value }}
          {...inputProps}
        />
        {isChecked && (
          <RadioDot className="vui-radioDot" h={dotSize[size] ?? dotSize.md} w={dotSize[size] ?? dotSize.md} />
        )}
      </RadioControl>

      {children ??
        (label && (
          <Box column>
            <T className="vui-radioLabel" color={labelColor} variant="caption" {...styles.label}>
              {label}
            </T>
            {description && (
              <T className="vui-radioDescription" color={descriptionColor} id={descriptionId} mt="2px" size="sm">
                {description as ReactNode}
              </T>
            )}
          </Box>
        ))}
    </RadioBase>
  )
})

Radio.displayName = 'Radio'

export default Radio
